import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/auth-context';
import { authApi } from '../../services/api';
import { getRememberedEmail } from '../../utils/remembered-email';
import { SignInView } from './SignInView';

const signupEnabled = import.meta.env.VITE_SIGNUP_ENABLED === 'true';

/**
 * IdP-initiated SSO entry point. The Okta catalog tile's launch URL points
 * here. We read the remembered email from a prior session and, if the user
 * is federated, redirect straight to the Okta authorize URL — skipping the
 * email form on /signin.
 */
export default function SsoLanding() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    if (isLoading || startedRef.current) return;

    if (isAuthenticated) {
      startedRef.current = true;
      navigate('/', { replace: true });
      return;
    }

    const email = getRememberedEmail().trim();
    if (!email) {
      startedRef.current = true;
      navigate('/signin', { replace: true });
      return;
    }

    startedRef.current = true;
    (async () => {
      try {
        const result = await authApi.discover(email);
        if (result.method === 'okta' && result.authorizeUrl) {
          window.location.href = result.authorizeUrl;
          return;
        }
        navigate('/signin', { replace: true, state: { email } });
      } catch {
        navigate('/signin', { replace: true });
      }
    })();
  }, [isAuthenticated, isLoading, navigate]);

  return (
    <SignInView
      step="redirecting"
      email=""
      password=""
      error=""
      isLoading={false}
      signupEnabled={signupEnabled}
      onEmailChange={() => {}}
      onPasswordChange={() => {}}
      onEmailSubmit={(e) => e.preventDefault()}
      onPasswordSubmit={(e) => e.preventDefault()}
      onUseDifferentEmail={() => {}}
    />
  );
}
